import { createFeature, createReducer, on } from '@ngrx/store';
import { CleverPushNotification, CleverPushTopic, CleverPushTopicType } from '@zero/feature-clever-push/base';
import { IBackendResponse } from '@zero/shared/util-models';
import { PriceAlertDetails, PriceAlertsOverview } from '../entities/price-alerts.entity';
import { alertingActions, alertingApiActions } from './alerting.actions';

export type AlertingActionsStatus = 'DELETE_SUCCESS' | 'UPDATE_SUCCESS' | 'UPDATING' | 'ERROR' | 'MAX_ITEMS_REACHED' | null;

export interface AlertingState {
  topics: Record<string, CleverPushTopic>;
  notifications: Record<string, CleverPushNotification<CleverPushTopicType | undefined>>;
  priceAlertOverview: PriceAlertsOverview[];
  selectedPriceAlert: PriceAlertDetails | null;
  selectedPriceAlertQuote: number | null;
  status: AlertingActionsStatus | null;
}

const initialState: AlertingState = {
  topics: {},
  notifications: {},
  priceAlertOverview: [],
  selectedPriceAlert: null,
  selectedPriceAlertQuote: null,
  status: null,
};

export const alertingFeature = createFeature({
  name: 'alerting',
  reducer: createReducer(
    initialState,
    on(alertingApiActions.loadTopicsSuccess, (state, { topics }): AlertingState => ({ ...state, topics })),
    on(
      alertingActions.setTopicSubscription,
      (state, { topicName, status }): AlertingState => ({
        ...state,
        topics: {
          ...state.topics,
          [topicName]: {
            ...state.topics[topicName],
            value: status,
          },
        },
      }),
    ),
    on(
      alertingApiActions.setTopicSubscriptionFailure,
      (state, { topicName }): AlertingState => ({
        ...state,
        topics: {
          ...state.topics,
          [topicName]: {
            ...state.topics[topicName],
            value: !state.topics[topicName].value,
          },
        },
      }),
    ),
    on(alertingActions.setNotifications, (state, { notifications }): AlertingState => ({ ...state, notifications })),
    on(
      alertingApiActions.loadPriceAlertOverviewSuccess,
      (state, { priceAlertOverview }): AlertingState => ({ ...state, priceAlertOverview }),
    ),
    on(
      alertingActions.loadPriceAlertDetails,
      (state): AlertingState => ({ ...state, selectedPriceAlert: null, selectedPriceAlertQuote: null }),
    ),
    on(
      alertingApiActions.loadPriceAlertDetailsSuccess,
      (state, { priceAlert }): AlertingState => ({ ...state, selectedPriceAlert: { ...priceAlert } }),
    ),
    on(
      alertingActions.updatePriceAlertDetails,
      alertingActions.createPriceAlert,
      (state): AlertingState => ({ ...state, status: 'UPDATING' }),
    ),
    on(
      alertingApiActions.updatePriceAlertDetailsSuccess,
      (state, { priceAlert }): AlertingState => ({
        ...state,
        selectedPriceAlert: {
          ...priceAlert,
          quoteAlerts: {
            ...priceAlert.quoteAlerts,
            alerts: priceAlert.quoteAlerts.alerts.filter((alert) => !alert.deleted),
          },
        },
        status: 'UPDATE_SUCCESS',
      }),
    ),
    on(
      alertingApiActions.createPriceAlertSuccess,
      (state, { priceAlert }): AlertingState => ({
        ...state,
        selectedPriceAlert: { ...priceAlert },
        priceAlertOverview: [...state.priceAlertOverview, { ...priceAlert, alerts: [] }],
        status: 'UPDATE_SUCCESS',
      }),
    ),
    on(
      alertingApiActions.updatePriceAlertDetailsFailure,
      alertingApiActions.createPriceAlertFailure,
      (state, { error }): AlertingState => ({
        ...state,
        status: (error.error as IBackendResponse<unknown>).errorCode === 'PRICEALERTS_MAX_ITEMS_REACHED' ? 'MAX_ITEMS_REACHED' : 'ERROR',
      }),
    ),
    on(
      alertingApiActions.deletePriceAlertSuccess,
      (state, { isin }): AlertingState => ({
        ...state,
        priceAlertOverview: state.priceAlertOverview.filter((priceAlert) => priceAlert.isin !== isin),
        status: 'DELETE_SUCCESS',
      }),
    ),
    on(
      alertingActions.updatePriceAlertQuote,
      (state, { quote }): AlertingState => ({
        ...state,
        selectedPriceAlertQuote: quote,
      }),
    ),
  ),
});
